import React from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useGlobal } from "@/context/GlobalContext";
import { Info, Loader2, X, Check } from "lucide-react";
import { ComposedChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList } from "recharts";

const fmt = (v) => (typeof v === "number" ? Math.round(v).toLocaleString("es-MX", { maximumFractionDigits: 0 }) : v);

export default function ReportView() {
  const { key } = useParams();
  const { structureKey, node, periodRange, currentStructure } = useGlobal();
  const enabled = !!structureKey;

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["report", key, structureKey, node?.id ?? "entire", periodRange.start],
    queryFn: async () =>
      (await api.get(`/reports/${key}`, {
        params: { structure: structureKey, node_id: node?.id, fecha_inicio: periodRange.start, fecha_fin: periodRange.end },
      })).data,
    enabled, retry: false,
  });

  if (!enabled) return <Empty msg="Selecciona una estructura en Estructura Organizacional." />;
  if (isLoading) return <div className="flex items-center justify-center py-32"><Loader2 className="w-8 h-8 text-red-600 animate-spin" /></div>;
  if (isError) {
    const code = error?.response?.status;
    return <Empty msg={code === 403 ? "No tienes permiso para consultar este reporte en el nodo seleccionado." : (formatApiErrorDetail(error?.response?.data?.detail) || "No se pudo cargar el reporte.")} />;
  }

  const subtitle = data.node.razon_social && data.node.razon_social !== data.node.name
    ? `${data.node.razon_social} · ${data.node.name}` : data.node.name;
  const periodTag = periodRange.label.replace(/^(\w{3})\w*\s(\d+)$/, (m, a, b) => `${a.toUpperCase()} ${b}`);

  return (
    <div className="w-full">
      <div className="rounded-2xl overflow-hidden shadow-2xl bg-[#f4f5f7] text-slate-900 border border-[#223048]" data-testid="report-sheet">
        {/* Header */}
        <div className="relative bg-gradient-to-b from-[#eef0f3] to-[#e6e8ec] px-4 sm:px-8 pt-4 sm:pt-5 pb-4 border-b border-slate-300">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-lg bg-white flex items-center justify-center shadow-md p-1.5 shrink-0">
                <img src="/logo-zae.png" alt="ZAE Ejecutivo" className="w-full h-full object-contain" />
              </div>
              <div className="min-w-0">
                <h1 className="font-display text-xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900 leading-tight break-words" data-testid="report-title">{data.report.name}</h1>
                <p className="text-blue-700 font-bold text-[11px] sm:text-sm mt-0.5 sm:mt-1 uppercase tracking-wide truncate">{subtitle}</p>
              </div>
            </div>
            <div className="text-right shrink-0">
              <p className="font-display font-black text-slate-800 text-base sm:text-lg leading-none" style={{ color: currentStructure?.color }}>ZAE</p>
              <p className="text-[9px] sm:text-[10px] text-slate-500 uppercase tracking-widest hidden xs:block sm:block max-w-[120px] sm:max-w-none truncate">{data.structure_name}</p>
            </div>
          </div>
        </div>

        {/* Contexto */}
        <div className="px-4 sm:px-8 py-2.5 bg-white/60 border-b border-slate-200 text-xs text-slate-600 flex flex-wrap items-center gap-x-2 gap-y-1" data-testid="report-context">
          <span className="font-semibold text-slate-700">{data.structure_name}</span>
          {data.node.ancestors?.map((a) => <span key={a.id}>› {a.name}</span>)}
          <span className="text-slate-900 font-semibold">› {data.node.name}</span>
          <span className="ml-auto px-2 py-0.5 rounded bg-slate-800 text-white font-semibold">{periodTag}</span>
        </div>

        {/* Body */}
        <div className="px-4 sm:px-8 py-6 min-h-[360px]">
          {data.layout === "comparativo" && <Comparativo data={data.data} unit={data.report.unit} />}
          {data.layout === "canal" && <Canal data={data.data} />}
          {data.layout === "vdcf" && <VentaDirecta data={data.data} />}
          {data.layout === "productividades" && <Productividades data={data.data} />}
          {data.layout === "consumidor" && <VentaConsumidor data={data.data} />}
          {data.layout === "ingresos" && <Ingresos data={data.data} />}
          {data.layout === "nomina" && <Nomina data={data.data} />}
          {data.layout === "presupuesto" && <PresupuestoPlanta data={data.data} />}
          {data.layout === "costo" && <CostoOperacion data={data.data} />}
          {data.layout === "comisiones" && <ComisionesDescuentos data={data.data} />}
          {data.layout === "cartera" && <Cartera data={data.data} />}
          {data.layout === "inversiones" && <Inversiones data={data.data} />}
          {data.layout === "resumen_gestion" && <ResumenGestion data={data.data} />}
          {(data.layout === "mensual_ventas" || data.layout === "mensual_hg") && <Mensual data={data.data} />}
          {data.layout === "historico" && <Historico data={data.data} />}
          {data.layout === "estado" && <Estado data={data.data} />}
          {data.layout === "estado_cents" && <EstadoCents data={data.data} />}
          {data.layout === "tabla" && <Tabla data={data.data} unit={data.report.unit} />}
        </div>

        {/* Footer */}
        <div className="relative flex items-center justify-between gap-2 px-4 sm:px-8 py-3 bg-gradient-to-r from-white to-slate-100 border-t border-slate-300">
          <p className="italic text-slate-500 text-[11px] sm:text-xs min-w-0 truncate">{data.report.nota}</p>
          <span className="px-3 py-1 bg-slate-900 text-slate-200 text-xs font-semibold" style={{ clipPath: "polygon(12% 0,100% 0,100% 100%,0 100%)" }}>{periodTag}</span>
        </div>
      </div>

      {data.report.origen === "odoo" ? (
        <div className="w-full mt-4 flex items-start gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 text-sm text-emerald-300/90" data-testid="report-source-live">
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <p><span className="font-semibold">Datos en vivo desde Odoo.</span> {data.report.nota}</p>
        </div>
      ) : (
        <div className="w-full mt-4 flex items-start gap-3 rounded-xl border border-amber-500/25 bg-amber-500/5 p-4 text-sm text-amber-200/90">
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <p><span className="font-semibold">Diseño listo (datos de muestra).</span> El reporte replica el formato del PowerPoint y ya recibe el contexto global (organización + periodo + permisos). Indícanos de dónde tomar los datos para conectarlos.</p>
        </div>
      )}
    </div>
  );
}

const HEAD = "bg-[#1f2a44] text-white text-xs font-semibold uppercase tracking-wide";
const neg = (v) => typeof v === "number" && v < 0;

function Comparativo({ data, unit }) {
  const u = unit === "%" ? "%" : "";
  const hasDias = data.rows.some((r) => r.dias != null);
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-separate border-spacing-y-2 min-w-[560px]">
        <thead>
          <tr>
            <th className="text-left w-1/3"></th>
            {hasDias && <th className={`${HEAD} px-3 py-2.5 rounded w-16`}>Días</th>}
            <th className={`${HEAD} px-4 py-2.5 rounded`}>Realizado</th>
            <th className={`${HEAD} px-4 py-2.5 rounded`}>Variaciones</th>
            <th className={`${HEAD} px-4 py-2.5 rounded w-24`}>Logros</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r, i) => (
            <tr key={i} data-testid={`comp-row-${i}`}>
              <td className="pr-4 py-1">
                <p className={`font-bold ${r.period === "Meta" ? "text-blue-700" : "text-slate-800"} text-lg leading-tight`}>{r.period}</p>
                <p className="text-[11px] text-slate-500">{r.label}</p>
              </td>
              {hasDias && <td className="text-center text-base font-semibold px-3 py-3 rounded bg-slate-100 text-slate-700" data-testid={`comp-dias-${i}`}>{r.dias ?? ""}</td>}
              <td className={`text-center text-xl font-bold px-4 py-3 rounded ${r.es_resultado ? "bg-[#bcd6ec] border-2 border-[#7fb0d8] text-slate-900" : "bg-slate-200/70 text-slate-800"}`}>
                {r.realizado == null ? "N/D" : `${fmt(r.realizado)}${u}`}
              </td>
              <td className={`text-center text-lg font-semibold px-4 py-3 rounded bg-slate-100 ${neg(r.variacion) ? "text-red-600" : "text-emerald-600"}`}>
                {r.variacion == null ? "" : `${r.variacion >= 0 ? "+ " : "- "}${fmt(Math.abs(r.variacion))}${u}`}
              </td>
              <td className="text-center px-4 py-3 rounded bg-slate-100">
                {r.logro == null ? "" : r.logro ? <Check className="w-5 h-5 text-emerald-600 mx-auto" /> : <X className="w-5 h-5 text-red-500 mx-auto" />}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Canal({ data }) {
  const cols = data.columns;
  const money = (v) => (v == null ? "N/D" : Math.round(v).toLocaleString("es-MX"));
  const headCls = (c) =>
    c.key === "total"
      ? "bg-slate-600 text-white"
      : c.group
      ? "bg-slate-400 text-white"
      : "text-slate-600";
  const bodyCls = (c) =>
    c.key === "total"
      ? "bg-slate-300 font-bold text-slate-900"
      : c.group
      ? "bg-slate-100 font-semibold text-slate-800"
      : "text-slate-700";
  return (
    <div className="overflow-x-auto space-y-8">
      <table className="w-full text-sm min-w-[960px] border-separate border-spacing-0" data-testid="canal-table">
        <thead>
          <tr>
            <th className="px-3 py-2 text-center text-slate-600 text-xs uppercase font-bold">Mes</th>
            {cols.map((c) => (
              <th key={c.key} className={`px-3 py-2.5 text-center align-bottom ${headCls(c)}`}>
                <span className="block text-[11px] font-bold leading-tight">{c.label}</span>
                {c.sub && <span className="block text-[9px] font-normal opacity-90">{c.sub}</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-100" data-testid={`canal-row-${i}`}>
              <td className="px-3 py-3 text-right whitespace-nowrap">
                <span className={`block font-bold text-base leading-tight ${r.period === "Meta" ? "text-blue-700" : "text-slate-800"}`}>{r.period}</span>
                <span className="block text-[10px] text-slate-500">{r.label}</span>
              </td>
              {cols.map((c) => (
                <td key={c.key} className={`px-3 py-3 text-center tabular-nums ${bodyCls(c)}`}>
                  {r.nd ? <span className="text-slate-400">N/D</span> : money(r.values[c.key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      <div>
        <p className="text-slate-700 font-extrabold mb-2 uppercase text-sm">Variaciones vs:</p>
        <table className="w-full text-sm min-w-[960px] border-separate border-spacing-0" data-testid="canal-var-table">
          <thead>
            <tr>
              <th className="px-3 py-2"></th>
              {cols.map((c) => (
                <th key={c.key} className={`px-3 py-2 text-center text-[11px] font-bold ${headCls(c)}`}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.var_rows.map((r, i) => (
              <tr key={i} className="border-b border-slate-100" data-testid={`canal-var-row-${i}`}>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                  <span className="block text-blue-700 font-semibold text-xs">vs {r.period}</span>
                  <span className="block text-[9px] text-slate-500">{r.label}</span>
                </td>
                {cols.map((c) => {
                  if (r.nd) return <td key={c.key} className={`px-3 py-2.5 text-center text-slate-400 ${bodyCls(c)}`}>N/D</td>;
                  const v = r.values[c.key] ?? 0;
                  return (
                    <td key={c.key} className={`px-3 py-2.5 text-center tabular-nums ${bodyCls(c)} ${v < 0 ? "text-red-600" : "text-emerald-600"}`}>
                      {v >= 0 ? "+ " : "- "}{money(Math.abs(v))}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const PROD_COLORS = { A: "#1f2a44", B: "#2b9fd6", C: "#b01e3c", TOTAL: "#12232f" };

function Productividades({ data }) {
  const cls = [...data.classes, "TOTAL"];
  const num = (v) => Math.round(v).toLocaleString("es-MX");
  return (
    <div className="overflow-x-auto space-y-6">
      <table className="text-sm border-separate border-spacing-0 min-w-[860px] w-full" data-testid="prod-table">
        <thead>
          <tr>
            <th className="px-3 py-2 text-left text-slate-600 text-xs uppercase font-bold">Mes</th>
            {cls.map((c) => (
              <React.Fragment key={c}>
                <th className="px-3 py-2.5 text-center text-white text-sm font-black" style={{ backgroundColor: PROD_COLORS[c] }}>{c === "TOTAL" ? "TOTAL" : `"${c}"`}</th>
                <th className="px-3 py-2.5 text-center text-slate-500 text-[10px] uppercase bg-slate-100 font-bold tracking-wide">Promedio</th>
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r, i) => (
            <tr key={i} data-testid={`prod-row-${i}`}>
              <td className="px-3 py-3 font-bold text-slate-800 text-base whitespace-nowrap">{r.cat}</td>
              {cls.map((c) => (
                <React.Fragment key={c}>
                  <td className={`px-3 py-3 text-center tabular-nums font-semibold border border-slate-200 ${c === "TOTAL" ? "bg-slate-100 font-bold" : ""}`}>{num(r.values[c].count)}</td>
                  <td className="px-3 py-3 text-center tabular-nums text-slate-500 border border-slate-100">{num(r.values[c].prom)}</td>
                </React.Fragment>
              ))}
            </tr>
          ))}
          <tr className="border-t-2 border-slate-300" data-testid="prod-total-row">
            <td className="px-3 py-3 font-bold text-slate-800 text-base">Total</td>
            {cls.map((c) => (
              <React.Fragment key={c}>
                <td className="px-3 py-3 text-center tabular-nums font-bold border border-slate-200">{num(data.totales[c].count)}</td>
                <td className="px-3 py-3 text-center tabular-nums text-slate-500 border border-slate-100">{num(data.totales[c].prom)}</td>
              </React.Fragment>
            ))}
          </tr>
          <tr data-testid="prod-pct-row">
            <td></td>
            {cls.map((c) => (
              <td key={c} colSpan={2} className="px-3 py-4 text-center text-white text-2xl font-black" style={{ backgroundColor: PROD_COLORS[c] }}>{data.pct[c]}%</td>
            ))}
          </tr>
        </tbody>
      </table>

      <div className="border-t border-slate-200 pt-4">
        <p className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-2">Reglas de clasificación (configurables)</p>
        <table className="text-xs" data-testid="prod-rules">
          <tbody>
            {data.reglas.map((rr, i) => (
              <tr key={i}>
                <td className="pr-4 py-1 font-bold text-slate-700 whitespace-nowrap">{rr.cat}</td>
                {["A", "B", "C"].map((c) => (
                  <td key={c} className="py-1 px-1">
                    <div className="flex items-stretch">
                      <span className="px-2 py-1 text-white font-bold" style={{ backgroundColor: PROD_COLORS[c] }}>"{c}"</span>
                      <span className="px-2 py-1 bg-slate-50 border border-slate-200 text-slate-700 whitespace-nowrap">{rr[c]}</span>
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function VentaDirecta({ data }) {
  const money = (v) => (v == null ? "N/D" : Math.round(v).toLocaleString("es-MX"));
  const pct = (v) => (v == null ? "N/D" : `${Math.round(v)}%`);
  return (
    <div className="overflow-x-auto flex justify-center">
      <table className="text-sm border-separate border-spacing-0 min-w-[600px]" data-testid="vdcf-table">
        <thead>
          <tr>
            <th className="px-3 py-2 text-right text-slate-600 text-xs uppercase font-bold">Mes</th>
            <th className="px-4 py-2.5 bg-slate-200 text-slate-700 text-center text-[11px] font-bold leading-tight">TOTAL<br />TERCEROS</th>
            <th className="px-3 py-2.5 bg-slate-200 text-slate-700 text-center text-[11px] font-bold">%</th>
            <th className="px-4 py-2.5 bg-[#2b9fd6] text-white text-center text-[11px] font-bold leading-tight">TOTAL<br />VDCF</th>
            <th className="px-3 py-2.5 bg-[#12232f] text-white text-center text-[11px] font-bold">%</th>
            <th className="px-4 py-2.5 bg-slate-500 text-white text-center text-[11px] font-bold leading-tight">VENTA<br />TOTAL</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-100" data-testid={`vdcf-row-${i}`}>
              <td className="px-3 py-2 text-right whitespace-nowrap bg-slate-50">
                <span className="text-[10px] text-slate-400 mr-2">{r.year}</span>
                <span className="font-bold text-slate-700 text-base">{r.mes}</span>
              </td>
              <td className="px-4 py-2 text-center tabular-nums text-slate-800">{money(r.terceros)}</td>
              <td className="px-3 py-2 text-center tabular-nums text-slate-600">{pct(r.pct_terceros)}</td>
              <td className="px-4 py-2 text-center tabular-nums text-slate-800">{money(r.vdcf)}</td>
              <td className="px-3 py-2 text-center tabular-nums font-bold text-white bg-[#2b9fd6]">{pct(r.pct_vdcf)}</td>
              <td className="px-4 py-2 text-center tabular-nums font-semibold text-slate-800 bg-slate-100">{money(r.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function VentaConsumidor({ data }) {
  const rows = data.rows || [];
  const num = (v) => (v == null ? "N/D" : Math.round(v).toLocaleString("es-MX"));
  const chart = rows.map((r) => ({ mes: r.mes, venta: r.venta, clientes: r.clientes }));
  return (
    <div className="space-y-6">
      <div className="inline-block px-3 py-1 bg-red-600 text-white text-sm font-bold uppercase tracking-wide rounded" data-testid="consumidor-title">Venta <span className="font-normal">(Ton)</span></div>
      <div className="h-64 w-full" data-testid="consumidor-chart">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chart} margin={{ top: 24, right: 20, left: 0, bottom: 0 }}>
            <XAxis dataKey="mes" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
            <YAxis yAxisId="v" hide domain={["dataMin - 400", "dataMax + 300"]} />
            <YAxis yAxisId="c" orientation="right" hide domain={[0, "dataMax + 8"]} />
            <Tooltip formatter={(val, name) => [num(val), name === "venta" ? "Venta (Ton)" : "Clientes nuevos"]} />
            <Line yAxisId="v" type="monotone" dataKey="venta" stroke="#e11d2a" strokeWidth={2.5} dot={{ r: 3, fill: "#e11d2a" }}>
              <LabelList dataKey="venta" position="top" formatter={num} style={{ fontSize: 10, fill: "#b91c1c" }} />
            </Line>
            <Line yAxisId="c" type="monotone" dataKey="clientes" stroke="#2563eb" strokeWidth={2.5} dot={{ r: 9, fill: "#2563eb" }}>
              <LabelList dataKey="clientes" position="center" style={{ fontSize: 9, fill: "#fff", fontWeight: 700 }} />
            </Line>
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="overflow-x-auto">
        <table className="text-sm border-separate border-spacing-0 min-w-[860px] w-full" data-testid="consumidor-table">
          <thead>
            <tr>
              <th className="px-2 py-2"></th>
              {rows.map((r, i) => (
                <th key={i} className="px-2 py-2 text-center text-slate-600 text-xs font-bold bg-slate-100 border border-slate-200">
                  <span className="block text-[9px] text-slate-400 font-normal">{r.year}</span>{r.mes}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr data-testid="consumidor-venta-row">
              <td className="px-2 py-2.5 text-[10px] uppercase font-bold text-slate-500 whitespace-nowrap">Venta (Ton)</td>
              {rows.map((r, i) => (
                <td key={i} className="px-2 py-2.5 text-center tabular-nums font-bold text-slate-800 border border-slate-100">{num(r.venta)}</td>
              ))}
            </tr>
            <tr data-testid="consumidor-pct-row">
              <td className="px-2 py-1.5 text-[10px] uppercase font-bold text-slate-500">%</td>
              {rows.map((r, i) => (
                <td key={i} className="px-2 py-1.5 text-center tabular-nums text-blue-600 border border-slate-100">{r.pct == null ? "N/D" : `${r.pct}%`}</td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      <div className="overflow-x-auto">
        <div className="inline-block px-3 py-1.5 bg-[#1f3a5f] text-white text-xs font-bold uppercase rounded-t">Clientes Nuevos</div>
        <table className="text-sm border-separate border-spacing-0 min-w-[860px] w-full" data-testid="consumidor-clientes-table">
          <tbody>
            {[["Crédito", "clientes_cred"], ["Contado", "clientes_cont"], ["Total", "clientes"]].map(([label, k], ri) => (
              <tr key={k} data-testid={`consumidor-clientes-${k}`}>
                <td className={`px-2 py-2 text-[10px] uppercase font-bold whitespace-nowrap border border-slate-200 ${ri === 2 ? "bg-[#1f3a5f] text-white" : "text-slate-500 bg-slate-50"}`}>{label}</td>
                {rows.map((r, i) => (
                  <td key={i} className={`px-2 py-2 text-center tabular-nums border border-slate-100 ${ri === 2 ? "font-bold text-slate-900 bg-slate-100" : "text-slate-700"}`}>{num(r[k])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="overflow-x-auto">
        <div className="inline-block px-3 py-1.5 bg-slate-600 text-white text-xs font-bold uppercase rounded-t">Venta Nueva (Tons)</div>
        <table className="text-sm border-separate border-spacing-0 min-w-[860px] w-full" data-testid="consumidor-ventanueva-table">
          <tbody>
            {[["Crédito", "venta_nueva_cred"], ["Contado", "venta_nueva_cont"], ["Total", "venta_nueva"]].map(([label, k], ri) => (
              <tr key={k} data-testid={`consumidor-ventanueva-${k}`}>
                <td className={`px-2 py-2 text-[10px] uppercase font-bold whitespace-nowrap border border-slate-200 ${ri === 2 ? "bg-slate-600 text-white" : "text-slate-500 bg-slate-50"}`}>{label}</td>
                {rows.map((r, i) => (
                  <td key={i} className={`px-2 py-2 text-center tabular-nums border border-slate-100 ${ri === 2 ? "font-bold text-slate-900 bg-slate-100" : "text-slate-700"}`}>{num(r[k])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {!data.venta_nueva_disponible && (
          <p className="text-[11px] text-slate-400 mt-1.5" data-testid="consumidor-vn-note">La venta nueva se calcula por planta o alcances pequeños; selecciona una planta para verla.</p>
        )}
      </div>
    </div>
  );
}

function Ingresos({ data }) {
  const m0 = (v) => (v == null ? "N/D" : Math.round(v).toLocaleString("es-MX"));
  const m2 = (v) => (v == null ? "N/D" : Number(v).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  const H = { dark: "bg-[#4b4b4b] text-white", gray: "bg-slate-300 text-slate-800", light: "bg-slate-200 text-slate-700", red: "bg-[#7a1420] text-white" };
  const B = { dark: "bg-slate-100", gray: "bg-slate-50", light: "", red: "" };
  const cols = [
    { k: "bancos", label: "Ingreso en Bancos", fmt: m0, g: "dark" },
    { k: "venta", label: "Venta", fmt: m0, g: "gray" },
    { k: "ing_kilo", label: "Ingreso por Kilo", fmt: m2, g: "gray", bold: true },
    { k: "precio", label: "Precio Oficial", fmt: m2, g: "light" },
    { k: "comisiones", label: "Comisiones y Descuentos", fmt: m2, g: "light" },
    { k: "cartera", label: "Cartera", fmt: m2, g: "light", neg: true },
    { k: "teorico", label: "Ingreso Teórico", fmt: m2, g: "dark", bold: true },
    { k: "falt_c", label: "Faltante \u00a2", fmt: m2, g: "red", neg: true },
    { k: "falt_i", label: "Faltante $", fmt: m0, g: "red", neg: true },
  ];
  const rows = [...(data.rows || []), { ...data.acumulado, _acum: true }];
  const cell = (c, r) => {
    const v = r[c.k];
    const red = c.neg && typeof v === "number" && v < 0;
    return `px-3 py-2.5 text-center tabular-nums border border-slate-100 ${c.bold ? "font-bold" : ""} ${red ? "text-red-600" : "text-slate-800"} ${r._acum ? "bg-[#fdf3d3] font-bold" : B[c.g]}`;
  };
  return (
    <div className="overflow-x-auto">
      <table className="text-sm border-separate border-spacing-0 min-w-[900px] w-full" data-testid="ingresos-table">
        <thead>
          <tr>
            <th className="px-3 py-3 bg-white"></th>
            {cols.map((c) => (
              <th key={c.k} className={`px-3 py-3 text-center text-[11px] font-bold leading-tight ${H[c.g]}`}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} data-testid={`ingresos-row-${r.mes}`}>
              <td className={`px-3 py-2.5 font-bold text-slate-700 whitespace-nowrap ${r._acum ? "bg-[#fdf3d3]" : "bg-slate-100"}`}>{r.mes}</td>
              {cols.map((c) => (
                <td key={c.k} className={cell(c, r)}>{c.fmt(r[c.k])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Inversiones({ data }) {
  const rows = data.rows || [];
  const acum = data.acumulado || {};
  const curYear = rows.length ? Math.max(...rows.map((r) => r.year)) : new Date().getFullYear();
  const int = (v) => (v == null ? "" : Math.round(v).toLocaleString("es-MX"));
  const money = (v) => (v == null ? "" : `$ ${int(v)}`);
  const c2 = (v) => (v == null ? "" : Number(v).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  const cols = [
    { k: "unidades", l1: "UNIDADES", l2: "(LETRAS)" },
    { k: "equipo", l1: "EQUIPO", l2: "PLANTAS, ESTACIONES, UNIDADES" },
    { k: "cilindros", l1: "CILINDROS, TANQUES", l2: "& INSTALACIONES" },
    { k: "otros", l1: "OTROS", l2: "" },
  ];
  return (
    <div className="overflow-x-auto">
      <table className="text-sm border-separate border-spacing-0 min-w-[1050px] w-full" data-testid="inversiones-table">
        <thead>
          <tr>
            <th className="px-4 py-3 text-right text-slate-500 uppercase text-xs bg-white">Mes</th>
            {cols.map((c) => (
              <th key={c.k} className="px-3 py-3 bg-sky-100 text-slate-700 text-center leading-tight">
                <span className="block text-[12px] font-bold">{c.l1}</span>
                <span className="block text-[9px] font-normal text-slate-500">{c.l2}</span>
              </th>
            ))}
            <th className="px-4 py-3 bg-[#7a1420] text-white text-center">TOTAL</th>
            <th className="px-4 py-3 bg-[#4b4b4b] text-white text-center">¢</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const mesBg = r.year === curYear ? "bg-orange-100" : "bg-sky-100";
            return (
              <tr key={i} data-testid={`inversiones-row-${i}`}>
                <td className={`px-4 py-2 whitespace-nowrap ${mesBg}`}>
                  <span className="text-[11px] text-slate-500 mr-2">{r.year}</span>
                  <span className="font-bold text-slate-800 text-base">{r.mes}</span>
                </td>
                {cols.map((c) => (
                  <td key={c.k} className="px-3 py-2 text-center tabular-nums text-slate-800 border border-slate-100">{int(r[c.k])}</td>
                ))}
                <td className="px-4 py-2 text-right tabular-nums font-semibold text-slate-900 bg-slate-200 border border-slate-100">{money(r.total)}</td>
                <td className="px-4 py-2 text-center font-bold text-slate-900">{c2(r.cents)}</td>
              </tr>
            );
          })}
          <tr data-testid="inversiones-acumulado" className="font-bold">
            <td className="px-4 py-2.5 text-right text-slate-700 uppercase text-xs bg-white">ACUMULADO</td>
            {cols.map((c) => <td key={c.k} className="bg-white border border-slate-100"></td>)}
            <td className="px-4 py-2.5 text-right tabular-nums text-white bg-[#1f3a5f] border border-slate-100">{money(acum.total)}</td>
            <td className="px-4 py-2.5 text-center text-slate-900 border border-slate-100">{c2(acum.cents)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function Cartera({ data }) {
  const rows = data.rows || [];
  const acum = data.acumulado || {};
  const curYear = rows.length ? Math.max(...rows.map((r) => r.year)) : new Date().getFullYear();
  const int = (v) => (v == null ? "" : Math.round(v).toLocaleString("es-MX"));
  const money = (v) => (v == null ? "" : `$ ${int(v)}`);
  const sign = (v) => (v == null ? "" : v >= 0 ? `+ ${int(v)}` : `- ${int(Math.abs(v))}`);
  const varCls = (v) => (v == null ? "" : v >= 0 ? "text-red-600" : "text-slate-800");
  return (
    <div className="overflow-x-auto">
      <table className="text-sm border-separate border-spacing-0 min-w-[1150px] w-full" data-testid="cartera-table">
        <thead>
          <tr>
            <th className="px-4 py-3 text-right text-slate-500 uppercase text-xs bg-white">Mes</th>
            <th className="px-3 py-3 bg-sky-100 text-slate-700 text-center text-xs font-bold">CRÉDITO</th>
            <th className="px-3 py-3 bg-sky-100 text-slate-700 text-center text-xs font-bold">COBRANZA</th>
            <th className="px-3 py-3 bg-sky-100 text-slate-700 text-center text-xs font-bold">NOTAS DE CRÉDITO</th>
            <th className="px-3 py-3 bg-sky-100 text-slate-700 text-center text-xs font-bold">SALDO</th>
            <th className="px-3 py-3 bg-[#7a1420] text-white text-center text-[11px] font-bold leading-tight">VARIACIÓN<span className="block">DEL SALDO</span></th>
            <th className="px-3 py-3 bg-[#4b4b4b] text-white text-center text-xs font-bold">CORRIENTE</th>
            <th className="px-3 py-3 bg-[#4b4b4b] text-white text-center text-xs font-bold">VENCIDO</th>
            <th className="px-3 py-3 bg-[#7a1420] text-white text-center text-[11px] font-bold leading-tight">VARIACIÓN<span className="block">VENCIDO</span></th>
          </tr>
        </thead>
        <tbody>
          {data.saldo_inicial != null && (
            <tr data-testid="cartera-inicial">
              <td className="px-4 py-2 bg-white"></td>
              <td className="bg-white"></td><td className="bg-white"></td><td className="bg-white"></td>
              <td className="px-3 py-2 text-right font-bold text-slate-800 bg-slate-200 border border-slate-100">{money(data.saldo_inicial)}</td>
              <td className="bg-white"></td><td className="bg-white"></td><td className="bg-white"></td><td className="bg-white"></td>
            </tr>
          )}
          {rows.map((r, i) => {
            const mesBg = r.year === curYear ? "bg-orange-100" : "bg-sky-100";
            return (
              <tr key={i} data-testid={`cartera-row-${i}`}>
                <td className={`px-4 py-2 whitespace-nowrap ${mesBg}`}>
                  <span className="text-[11px] text-slate-500 mr-2">{r.year}</span>
                  <span className="font-bold text-slate-800 text-base">{r.mes}</span>
                </td>
                <td className="px-3 py-2 text-center tabular-nums text-slate-800 border border-slate-100">{int(r.credito)}</td>
                <td className="px-3 py-2 text-center tabular-nums text-slate-800 border border-slate-100">{int(r.cobranza)}</td>
                <td className="px-3 py-2 text-center tabular-nums text-slate-800 border border-slate-100">{int(r.notas)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-bold text-slate-900 bg-slate-100 border border-slate-100">{money(r.saldo)}</td>
                <td className={`px-3 py-2 text-right tabular-nums font-semibold bg-[#fdeef0] border border-slate-100 ${varCls(r.variacion)}`}>{sign(r.variacion)}</td>
                <td className="px-3 py-2 text-center tabular-nums text-slate-800 border border-slate-100">{int(r.corriente)}</td>
                <td className="px-3 py-2 text-center tabular-nums text-slate-800 border border-slate-100">{int(r.vencido)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-800 bg-slate-50 border border-slate-100">{sign(r.var_vencido)}</td>
              </tr>
            );
          })}
          <tr data-testid="cartera-acumulado" className="font-bold">
            <td className="px-4 py-2.5 bg-emerald-100 text-emerald-900">ACUMULADO</td>
            <td className="px-3 py-2.5 text-right tabular-nums text-slate-900 bg-slate-50 border border-slate-100">{money(acum.credito)}</td>
            <td className="px-3 py-2.5 text-right tabular-nums text-slate-900 bg-slate-50 border border-slate-100">{money(acum.cobranza)}</td>
            <td className="px-3 py-2.5 text-right tabular-nums text-slate-900 bg-slate-50 border border-slate-100">{money(acum.notas)}</td>
            <td className="px-3 py-2.5 text-right tabular-nums text-slate-900 bg-slate-200 border border-slate-100">{money(acum.saldo)}</td>
            <td className={`px-3 py-2.5 text-right tabular-nums bg-[#fdeef0] border border-slate-100 ${varCls(acum.variacion)}`}>{sign(acum.variacion)}</td>
            <td className="px-3 py-2.5 text-center text-slate-500 bg-slate-50 border border-slate-100">$ -</td>
            <td className="px-3 py-2.5 text-center text-slate-500 bg-slate-50 border border-slate-100">$ -</td>
            <td className="px-3 py-2.5 text-right tabular-nums text-slate-800 bg-slate-50 border border-slate-100">{sign(acum.var_vencido)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function ComisionesDescuentos({ data }) {
  const rows = data.rows || [];
  const curYear = rows.length ? Math.max(...rows.map((r) => r.year)) : new Date().getFullYear();
  const int = (v) => (v == null ? "" : Math.round(v).toLocaleString("es-MX"));
  const c2 = (v) => (v == null ? "" : Number(v).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  return (
    <div className="overflow-x-auto">
      <table className="text-sm border-separate border-spacing-0 min-w-[820px] w-full" data-testid="comisiones-table">
        <thead>
          <tr>
            <th className="px-4 py-3 text-right text-slate-500 uppercase text-xs bg-white">Mes</th>
            <th className="px-4 py-3 bg-[#1f3a5f] text-white text-center text-xs">COMISIONES</th>
            <th className="px-3 py-3 bg-sky-100 text-slate-700 text-center">¢</th>
            <th className="px-4 py-3 bg-[#1f3a5f] text-white text-center text-xs">DESCUENTOS</th>
            <th className="px-3 py-3 bg-sky-100 text-slate-700 text-center">¢</th>
            <th className="px-4 py-3 bg-[#7a1420] text-white text-center">TOTAL</th>
            <th className="px-4 py-3 bg-[#4b4b4b] text-white text-center">¢</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const mesBg = r.year === curYear ? "bg-orange-100" : "bg-sky-100";
            return (
              <tr key={i} data-testid={`comisiones-row-${i}`}>
                <td className={`px-4 py-2 whitespace-nowrap ${mesBg}`}>
                  <span className="text-[11px] text-slate-500 mr-2">{r.year}</span>
                  <span className="font-bold text-slate-800 text-base">{r.mes}</span>
                </td>
                <td className="px-4 py-2 text-center tabular-nums text-slate-800 border border-slate-100">{int(r.comisiones)}</td>
                <td className="px-3 py-2 text-center font-semibold text-slate-900 bg-sky-50 border border-slate-100">{c2(r.comisiones_c)}</td>
                <td className="px-4 py-2 text-center tabular-nums text-slate-800 border border-slate-100">{int(r.descuentos)}</td>
                <td className="px-3 py-2 text-center font-semibold text-slate-900 bg-sky-50 border border-slate-100">{c2(r.descuentos_c)}</td>
                <td className="px-4 py-2 text-right tabular-nums font-semibold text-slate-900 bg-slate-200">{r.total == null ? "" : `$ ${int(r.total)}`}</td>
                <td className="px-4 py-2 text-center font-bold text-slate-900">{c2(r.cents)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CostoOperacion({ data }) {
  const rows = data.rows || [];
  const curYear = rows.length ? Math.max(...rows.map((r) => r.year)) : new Date().getFullYear();
  const int = (v) => (v == null ? "" : Math.round(v).toLocaleString("es-MX"));
  const c2 = (v) => (v == null ? "" : Number(v).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  const cols = [
    { k: "presupuesto", l1: "PRESUPUESTO", l2: "PLANTA" },
    { k: "extraordinarios", l1: "EXTRAORDINARIOS", l2: "DISCRECIONALES Y NO DISCRECIONALES" },
    { k: "bancarias", l1: "COMISIONES", l2: "E INTERESES" },
    { k: "provision", l1: "PROVISIÓN", l2: "PLANTA" },
  ];
  return (
    <div className="overflow-x-auto">
      <table className="text-sm border-separate border-spacing-0 min-w-[880px] w-full" data-testid="costo-table">
        <thead>
          <tr>
            <th className="px-4 py-3 text-right text-slate-500 uppercase text-xs bg-white">Mes</th>
            {cols.map((c) => (
              <th key={c.k} className="px-3 py-3 bg-sky-100 text-slate-700 text-center leading-tight">
                <span className="block text-[12px] font-bold">{c.l1}</span>
                <span className="block text-[9px] font-normal text-slate-500">{c.l2}</span>
              </th>
            ))}
            <th className="px-4 py-3 bg-[#7a1420] text-white text-center">TOTAL</th>
            <th className="px-4 py-3 bg-[#4b4b4b] text-white text-center">¢</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const mesBg = r.year === curYear ? "bg-orange-100" : "bg-sky-100";
            return (
              <tr key={i} data-testid={`costo-row-${i}`}>
                <td className={`px-4 py-2 whitespace-nowrap ${mesBg}`}>
                  <span className="text-[11px] text-slate-500 mr-2">{r.year}</span>
                  <span className="font-bold text-slate-800 text-base">{r.mes}</span>
                </td>
                {cols.map((c) => (
                  <td key={c.k} className="px-3 py-2 text-center tabular-nums text-slate-800 border border-slate-100">{int(r[c.k])}</td>
                ))}
                <td className="px-4 py-2 text-right tabular-nums font-semibold text-slate-900 bg-slate-200">{r.total == null ? "" : `$ ${int(r.total)}`}</td>
                <td className="px-4 py-2 text-center font-bold text-slate-900">{c2(r.cents)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ResumenGestion({ data }) {
  const rows = data.rows || [];
  const year = data.year;
  const prev = data.prev_year;
  const nf = (v, d = 0) => Number(v).toLocaleString("es-MX", { minimumFractionDigits: d, maximumFractionDigits: d });
  const fmt = (v, unit) => {
    if (v == null) return "";
    if (unit === "ton") return nf(v);
    if (unit === "pct") return `${nf(v, 2)}%`;
    if (unit === "cent") return `${nf(v, 2)} ¢`;
    if (unit === "money") return `$ ${nf(v)}`;
    return nf(v);
  };
  const fmtDiff = (v, unit) => {
    if (v == null) return "";
    const s = v >= 0 ? "+ " : "- ";
    const a = Math.abs(v);
    if (unit === "pct") return `${s}${nf(a, 2)}%`;
    if (unit === "cent") return `${s}${nf(a, 2)} ¢`;
    if (unit === "money") return `${s}$ ${nf(a)}`;
    return `${s}${nf(a)}`;
  };
  const diffColor = (v) => (v == null ? "text-slate-400" : v < 0 ? "text-red-600" : "text-emerald-600");
  return (
    <div className="overflow-x-auto">
      <table className="text-sm border-separate border-spacing-0 min-w-[860px] w-full" data-testid="resumen-gestion-table">
        <thead>
          <tr>
            <th className="px-4 py-3 text-left text-slate-500 uppercase text-xs bg-white">Mes {data.mes}</th>
            <th className="px-4 py-3 bg-slate-700 text-white text-center">REAL<span className="block text-[11px] font-normal">{year}</span></th>
            <th className="px-4 py-3 bg-[#1f3a5f] text-white text-center">METAS<span className="block text-[11px] font-normal">{year}</span></th>
            <th className="px-4 py-3 bg-slate-500 text-white text-center">REAL<span className="block text-[11px] font-normal">{prev}</span></th>
            <th className="px-4 py-3 bg-[#4b4b4b] text-white text-center text-[11px] leading-tight">DIFERENCIA<span className="block font-normal">VS META</span></th>
            <th className="px-4 py-3 bg-[#4b4b4b] text-white text-center text-[11px] leading-tight">DIFERENCIA<span className="block font-normal">VS {prev}</span></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} data-testid={`resumen-row-${r.key}`} className="border-b border-slate-100">
              <td className="px-4 py-2.5 font-bold text-slate-700 uppercase text-xs bg-sky-50 border border-slate-100">{r.label}</td>
              <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-slate-900 border border-slate-100">{fmt(r.real, r.unit)}</td>
              <td className="px-4 py-2.5 text-right tabular-nums text-slate-800 bg-slate-50 border border-slate-100">{fmt(r.meta, r.unit)}</td>
              <td className="px-4 py-2.5 text-right tabular-nums text-slate-800 border border-slate-100">{fmt(r.prev, r.unit)}</td>
              <td className={`px-4 py-2.5 text-right tabular-nums font-medium border border-slate-100 ${diffColor(r.vs_meta)}`}>{fmtDiff(r.vs_meta, r.unit)}</td>
              <td className={`px-4 py-2.5 text-right tabular-nums font-medium border border-slate-100 ${diffColor(r.vs_prev)}`}>{fmtDiff(r.vs_prev, r.unit)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-[11px] text-slate-400 mt-3" data-testid="resumen-gestion-note">Costo de operación incluye: nómina, impuestos planta, gasto planta, ext. disc. y no disc. · REAL desde BIFCO · METAS desde Gestión Financiera (pesos → ¢/kg ÷ meta de venta) · %HG Neto y Cartera: meta pendiente de fuente en BIFCO.</p>
    </div>
  );
}

function PresupuestoPlanta({ data }) {
  const rows = data.rows || [];
  const curYear = rows.length ? Math.max(...rows.map((r) => r.year)) : new Date().getFullYear();
  const int = (v) => (v == null ? "" : Math.round(v).toLocaleString("es-MX"));
  const c2 = (v) => (v == null ? "" : Number(v).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  const cols = [
    { k: "sueldos", l: "NÓMINA" },
    { k: "rentas", l: "RENTAS" },
    { k: "servicios", l: "SERVICIOS" },
    { k: "taller", l: "TALLER" },
    { k: "mtto", l: "MTTO" },
    { k: "generales", l: "GASTOS GENERALES" },
    { k: "impuestos", l: "IMPUESTOS" },
  ];
  return (
    <div className="overflow-x-auto">
      <table className="text-sm border-separate border-spacing-0 min-w-[1050px] w-full" data-testid="presupuesto-table">
        <thead>
          <tr>
            <th className="px-4 py-3 text-right text-slate-500 uppercase text-xs bg-white">Mes</th>
            {cols.map((c) => (
              <th key={c.k} className="px-3 py-3 bg-sky-100 text-slate-700 text-center text-[11px] font-bold leading-tight">{c.l}</th>
            ))}
            <th className="px-4 py-3 bg-[#7a1420] text-white text-center">TOTAL</th>
            <th className="px-4 py-3 bg-[#4b4b4b] text-white text-center">¢</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const mesBg = r.year === curYear ? "bg-orange-100" : "bg-sky-100";
            return (
              <tr key={i} data-testid={`presupuesto-row-${i}`}>
                <td className={`px-4 py-2 whitespace-nowrap ${mesBg}`}>
                  <span className="text-[11px] text-slate-500 mr-2">{r.year}</span>
                  <span className="font-bold text-slate-800 text-base">{r.mes}</span>
                </td>
                {cols.map((c) => (
                  <td key={c.k} className="px-3 py-2 text-center tabular-nums text-slate-800 border border-slate-100">{int(r[c.k])}</td>
                ))}
                <td className="px-4 py-2 text-right tabular-nums font-semibold text-slate-900 bg-slate-200">{r.total == null ? "" : `$ ${int(r.total)}`}</td>
                <td className="px-4 py-2 text-center font-bold text-slate-900">{c2(r.cents)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Nomina({ data }) {
  const rows = data.rows || [];
  const curYear = rows.length ? Math.max(...rows.map((r) => r.year)) : new Date().getFullYear();
  const int = (v) => (v == null ? "" : Math.round(v).toLocaleString("es-MX"));
  const c2 = (v) => (v == null ? "" : Number(v).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  const groups = [
    { k: "admin", n: "emp_admin", l1: "NÓMINA", l2: "ADMINISTRATIVA" },
    { k: "op_portatil", n: "emp_portatil", l1: "OPERATIVA", l2: "PORTÁTIL" },
    { k: "op_estacionario", n: "emp_estacionario", l1: "OPERATIVA", l2: "ESTACIONARIO" },
    { k: "op_carburacion", n: "emp_carburacion", l1: "OPERATIVA", l2: "CARBURACIÓN" },
    { k: "op_otros", n: "emp_otros", l1: "OPERATIVA", l2: "OTROS PLANTA" },
  ];
  return (
    <div className="overflow-x-auto">
      <table className="text-sm border-separate border-spacing-0 min-w-[1200px] w-full" data-testid="nomina-table">
        <thead>
          <tr>
            <th className="px-4 py-3 text-right text-slate-500 uppercase text-xs bg-white">Mes</th>
            <th className="px-4 py-2.5 bg-[#7a1420] text-white text-center text-xs leading-tight">NÓMINA<span className="block">TOTAL</span></th>
            <th className="px-3 py-2.5 bg-[#4b4b4b] text-white text-center align-middle">¢</th>
            <th className="px-3 py-2.5 bg-slate-100 text-slate-500 text-center font-bold">#</th>
            {groups.map((g) => (
              <React.Fragment key={g.k}>
                <th className="px-4 py-2.5 bg-[#c01829] text-white text-center text-[11px] leading-tight">{g.l1}<span className="block font-normal">{g.l2}</span></th>
                <th className="px-3 py-2.5 bg-slate-100 text-slate-500 text-center font-bold">#</th>
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const mesBg = r.year === curYear ? "bg-orange-100" : "bg-sky-100";
            return (
              <tr key={i} data-testid={`nomina-row-${i}`}>
                <td className={`px-4 py-2 whitespace-nowrap ${mesBg}`}>
                  <span className="text-[11px] text-slate-500 mr-2">{r.year}</span>
                  <span className="font-bold text-slate-800 text-base">{r.mes}</span>
                </td>
                <td className="px-4 py-2 text-center bg-slate-200 tabular-nums font-semibold text-slate-800">{int(r.total)}</td>
                <td className="px-3 py-2 text-center font-bold text-slate-900">{c2(r.cents)}</td>
                <td className="px-3 py-2 text-center bg-sky-50 border border-slate-100 tabular-nums text-slate-700">{r.emp_total == null ? "" : r.emp_total}</td>
                {groups.map((g) => (
                  <React.Fragment key={g.k}>
                    <td className="px-4 py-2 text-center bg-slate-50 border border-slate-100 tabular-nums text-slate-800">{int(r[g.k])}</td>
                    <td className="px-3 py-2 text-center bg-sky-50 border border-slate-100 tabular-nums text-slate-700">{r[g.n] == null ? "" : r[g.n]}</td>
                  </React.Fragment>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Mensual({ data }) {
  return (
    <div className="overflow-x-auto flex justify-center">
      <table className="text-sm min-w-[440px]">
        <thead>
          <tr>
            <th className="px-4 py-2 text-slate-600 text-xs uppercase">Mes</th>
            <th className="px-4 py-2.5 bg-[#1f2a44] text-white text-center">Venta<span className="block text-[10px] font-normal">(Ton)</span></th>
            {data.with_hg && <th className="px-4 py-2.5 bg-[#1f2a44] text-white text-center">HG<span className="block text-[10px] font-normal">Kilos</span></th>}
            {data.with_hg && <th className="px-4 py-2.5 bg-slate-900 text-white text-center">HGN</th>}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-200" data-testid={`mensual-row-${i}`}>
              <td className="px-4 py-2"><span className="text-[10px] text-slate-500 mr-2">{r.year}</span><span className="font-bold text-slate-800">{r.mes}</span></td>
              <td className="px-4 py-2 text-center font-semibold">{fmt(r.venta_ton)}</td>
              {data.with_hg && <td className="px-4 py-2 text-center font-semibold">{fmt(r.hg_kilos)}</td>}
              {data.with_hg && <td className="px-4 py-2 text-center font-bold text-white bg-[#b01e3c]">{r.hgn}%</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EstadoCents({ data }) {
  const rows = data.rows || [];
  const nf0 = (v) => (v == null ? "" : Number(v).toLocaleString("es-MX", { maximumFractionDigits: 0 }));
  const nf2 = (v) => (v == null ? "" : Number(v).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  const cols = ["VENTA", "PV", "CV", "FLETE", "MARGEN", "COSTO OPERACIÓN", "COMISIONES Y DESCUENTOS",
    "IMPUESTOS", "HG", "UTILIDAD DE OPERACIÓN", "INVERSIÓN", "CORPORATIVOS", "RESULTADO FINAL"];
  const cell = (v, hi) => <td className={`px-2 py-2 text-right text-xs whitespace-nowrap ${v < 0 ? "text-red-600 font-semibold" : ""} ${hi ? "font-bold bg-slate-200" : ""}`}>{nf2(v)}</td>;
  return (
    <div className="overflow-x-auto">
      <table className="text-xs min-w-[1100px] w-full" data-testid="estado-cents-table">
        <thead>
          <tr>
            <th className="px-2 py-2 text-left text-slate-600 uppercase">Mes</th>
            {cols.map((c) => <th key={c} className="px-2 py-2 bg-[#1f2a44] text-white text-center text-[10px] leading-tight">{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} data-testid={`estado-cents-row-${i}`} className={r.kind === "anual" ? "bg-sky-50 font-medium" : (i % 2 ? "bg-slate-50" : "")}>
              <td className="px-2 py-2 font-semibold text-slate-800 whitespace-nowrap">{r.mes}</td>
              <td className="px-2 py-2 text-right tabular-nums">{nf0(r.venta)}</td>
              <td className="px-2 py-2 text-right">{nf2(r.pv)}</td>
              <td className="px-2 py-2 text-right">{nf2(r.cv)}</td>
              <td className="px-2 py-2 text-right">{nf2(r.flete)}</td>
              <td className="px-2 py-2 text-center font-bold text-white bg-[#2b9fd6]">{nf2(r.margen)}</td>
              {cell(r.costo)}{cell(r.comisiones)}{cell(r.impuestos)}{cell(r.hg)}
              {cell(r.utilidad, true)}
              {cell(r.inversion)}{cell(r.corporativos)}
              {cell(r.final, true)}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-[11px] text-slate-400 mt-3" data-testid="estado-cents-note">Valores en ¢/kilo (Venta en KG). Utilidad de Operación = Margen − ¢Costo Op − ¢Comisiones − ¢Impuestos − ¢HG. Resultado Final = ¢Utilidad − ¢Inversión − ¢Corporativos. Fuente: Odoo BIFCO.</p>
    </div>
  );
}

function Estado({ data }) {
  const cell = (v, i) => <td key={i} className={`px-2 py-2 text-right text-xs whitespace-nowrap ${neg(v) ? "text-red-600 font-semibold" : ""}`}>{fmt(v)}</td>;
  return (
    <div className="overflow-x-auto">
      <table className="text-xs min-w-[1050px] w-full">
        <thead>
          <tr>
            <th className="px-2 py-2 text-left text-slate-600 uppercase">Mes</th>
            {data.columns.map((c) => <th key={c} className="px-2 py-2 bg-[#1f2a44] text-white text-center text-[10px] leading-tight">{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r, i) => (
            <tr key={i} className={i % 2 ? "bg-slate-100" : ""} data-testid={`estado-row-${i}`}>
              <td className="px-2 py-2 font-semibold text-slate-800">{r.mes}</td>
              {cell(r.venta, 0)}
              <td className="px-2 py-2 text-right">{r.pv}</td>
              <td className="px-2 py-2 text-right">{r.cv}</td>
              <td className="px-2 py-2 text-right">{r.flete}</td>
              <td className="px-2 py-2 text-center font-bold text-white bg-[#2b9fd6]">{r.margen}</td>
              {cell(r.costo_op, 1)}{cell(r.comisiones, 2)}{cell(r.impuestos, 3)}{cell(r.hg, 4)}
              <td className="px-2 py-2 text-right font-bold bg-slate-200">{fmt(r.utilidad)}</td>
              {cell(r.inversion, 5)}{cell(r.corporativos, 6)}
              <td className={`px-2 py-2 text-right font-bold bg-slate-200 ${neg(r.final) ? "text-red-600" : "text-slate-900"}`}>{fmt(r.final)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Tabla({ data }) {
  return (
    <div className="overflow-x-auto flex justify-center">
      <table className="text-sm min-w-[520px]">
        <thead>
          <tr>{data.columns.map((c, i) => <th key={c} className={`px-4 py-2.5 text-white ${i === 0 ? "bg-slate-700 text-left" : "bg-[#1f2a44] text-center"}`}>{c}</th>)}</tr>
        </thead>
        <tbody>
          {data.rows.map((row, i) => (
            <tr key={i} className="border-b border-slate-200" data-testid={`tabla-row-${i}`}>
              {row.map((cell, j) => {
                const isDiff = (data.diff_cols || []).includes(j);
                const val = isDiff && typeof cell === "number" && cell < 0;
                return <td key={j} className={`px-4 py-2 ${j === 0 ? "text-left font-semibold text-slate-800" : "text-right"} ${val ? "text-red-600 font-semibold" : ""}`}>{j >= (data.numeric_from || 1) && typeof cell === "number" ? fmt(cell) : cell}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Historico({ data }) {
  const money = (v) => Number(v).toLocaleString("es-MX", { maximumFractionDigits: 0 });
  const signed = (v) => (v >= 0 ? "+ " : "- ") + money(Math.abs(v));
  const lastY = data.years[data.years.length - 1];
  return (
    <div className="flex flex-col xl:flex-row gap-8 justify-center">
      <div className="overflow-x-auto">
        <table className="text-sm border-separate border-spacing-0" data-testid="historico-table">
          <thead>
            <tr>
              <th className="px-3 py-2 text-left text-slate-500 text-xs">MES</th>
              {data.years.map((y) => (
                <th key={y} className={`px-3 py-2 text-center font-bold ${y === lastY ? "bg-[#7a1220] text-white" : "text-slate-800"}`}>{y}</th>
              ))}
              <th className="px-3 py-2 text-center font-bold bg-[#f3c9a0] text-slate-800">METAS {lastY}</th>
            </tr>
          </thead>
          <tbody>
            {data.months.map((m) => (
              <tr key={m.mes} className="border-b border-slate-100">
                <td className="px-3 py-1.5 text-slate-500 font-medium">{m.mes}</td>
                {data.years.map((y) => (
                  <td key={y} className={`px-3 py-1.5 text-right tabular-nums ${y === lastY && m.future ? "text-slate-300" : "text-slate-800"}`}>{money(m.vals[y])}</td>
                ))}
                <td className="px-3 py-1.5 text-right tabular-nums text-slate-800">{money(m.meta)}</td>
              </tr>
            ))}
            <tr className="bg-[#7a1220] text-white font-semibold">
              <td className="px-3 py-1.5 rounded-l">Anual</td>
              {data.years.map((y) => <td key={y} className="px-3 py-1.5 text-right tabular-nums">{money(data.anual[y])}</td>)}
              <td className="px-3 py-1.5 text-right tabular-nums">{money(data.anual.meta)}</td>
            </tr>
            <tr className="bg-[#2f7fbf] text-white font-semibold">
              <td className="px-3 py-1.5 rounded-l">ENE-JUN</td>
              {data.years.map((y) => <td key={y} className="px-3 py-1.5 text-right tabular-nums">{money(data.ene_jun[y])}</td>)}
              <td className="px-3 py-1.5 text-right tabular-nums">{money(data.ene_jun.meta)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="overflow-x-auto">
        <p className="text-slate-700 font-extrabold mb-1 uppercase text-sm">Variaciones {lastY} Vs:</p>
        <table className="text-sm border-separate border-spacing-0" data-testid="historico-var">
          <thead>
            <tr>
              <th className="px-3 py-2"></th>
              {data.var_cols.map((c) => <th key={c} className="px-3 py-2 text-center font-bold bg-slate-600 text-white min-w-[92px]">{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.var_months.map((m) => (
              <tr key={m.mes} className="border-b border-slate-100">
                <td className="px-3 py-1.5 text-slate-500 font-medium">{m.mes}</td>
                {["meta", "y2", "y1"].map((k) => <td key={k} className={`px-3 py-1.5 text-right tabular-nums font-medium ${m[k] < 0 ? "text-red-600" : "text-emerald-600"}`}>{signed(m[k])}</td>)}
              </tr>
            ))}
            <tr className="bg-[#7a1220] text-white font-semibold">
              <td className="px-3 py-1.5">Anual</td>
              {["meta", "y2", "y1"].map((k) => <td key={k} className="px-3 py-1.5 text-right tabular-nums">{signed(data.var_anual[k])}</td>)}
            </tr>
            <tr className="bg-[#2f7fbf] text-white font-semibold">
              <td className="px-3 py-1.5">ENE-JUN</td>
              {["meta", "y2", "y1"].map((k) => <td key={k} className="px-3 py-1.5 text-right tabular-nums">{signed(data.var_ene_jun[k])}</td>)}
            </tr>
            <tr className="bg-[#2f7fbf] text-white font-semibold">
              <td className="px-3 py-1.5">%</td>
              {["meta", "y2", "y1"].map((k) => <td key={k} className="px-3 py-1.5 text-right tabular-nums">{Math.round(data.var_pct[k])}%</td>)}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Empty({ msg }) {
  return (
    <div className="flex flex-col items-center justify-center py-28 text-center">
      <div className="w-14 h-14 rounded-2xl bg-red-600/10 text-red-500 flex items-center justify-center mb-4"><Info className="w-7 h-7" /></div>
      <p className="text-slate-300 max-w-md" data-testid="report-empty">{msg}</p>
    </div>
  );
}
